import { useEffect, useState } from 'react'
import classNames from 'classnames'
import { confirmBase } from 'utils/ui'
import i18n from 'i18n'
import dataManager from 'pages/main/data/DataManager'
import settings from 'popups/Settings'
import type { SendStatusResponse } from 'services/read-receipt'
import { timeFormat } from 'utils'
import {
  ReadReceiptDetailPopup,
  type ReadReceiptPopupRecipient,
} from '@owma/common-web/components/ReadReceiptDetailPopup'

import { subscribeReadReceipt } from '../readReceiptListenerStore'

import styles from './style.module.scss'

import closeIcon24 from 'assets/images/icon-close-24.svg'

function formatDateTime(iso: string): string {
  const { dateTimeFormat } = settings.getTimeFormat()
  return timeFormat(iso, dateTimeFormat)
}

function RecipientAvatar({ email, name }: { email: string; name?: string | null }) {
  const [avatar, setAvatar] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    // host 측에서는 viewerExt.NineNative.getAvatar 의 내부 구현과 동일하게 직접 조회
    dataManager
      .getAddressObj(email, name ?? '')
      .getInfoForAvatar()
      .then((info: any) => {
        if (cancelled || info == null) return
        if (info.avatarUrl != null) {
          setAvatar(info.avatarUrl)
        }
      })
      .catch(() => {
        // 아바타 로드 실패 시 placeholder 유지
      })
    return () => {
      cancelled = true
    }
  }, [email, name])

  if (avatar == null) {
    return <div className={classNames(styles.avatar, styles.avatarPlaceholder)} aria-hidden />
  }
  return <img src={avatar} className={classNames(styles.avatar)} alt="" width={32} height={32} />
}

function renderAvatar(recipient: ReadReceiptPopupRecipient) {
  return (
    <RecipientAvatar email={recipient.recipient_email} name={recipient.recipient_display_name} />
  )
}

interface ContentHostProps {
  emailId: string
  initialStatus: SendStatusResponse
}

/**
 * 셸측 래퍼 — 공용 본문(@owma/common-web ReadReceiptDetailPopup)에 host 의존을 주입한다.
 *
 * emailId 를 readReceiptListenerStore 에 구독해 refresh 결과를 자동 반영:
 * - notify(status=null) → 갱신 시작(로딩 인디케이터 표시, 본문 유지)
 * - notify(status=SendStatusResponse) → 본문 데이터 갱신
 */
function ReadReceiptDetailContentHost({ emailId, initialStatus }: ContentHostProps) {
  const [status, setStatus] = useState<SendStatusResponse>(initialStatus)
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false)

  useEffect(() => {
    const unsubscribe = subscribeReadReceipt(emailId, (newStatus) => {
      if (newStatus == null) {
        setIsRefreshing(true)
        return
      }
      setStatus(newStatus)
      setIsRefreshing(false)
    })
    return unsubscribe
  }, [emailId])

  return (
    <ReadReceiptDetailPopup
      status={status}
      isRefreshing={isRefreshing}
      /* i18next-extract-disable-next-line -- 실제 키는 공용 컴포넌트가 리터럴로 조회 */
      t={(key, opts) => i18n.t(key, opts)}
      formatDateTime={formatDateTime}
      renderAvatar={renderAvatar}
    />
  )
}

/**
 * 수신확인 메일 상세 팝업. (host 컨텍스트 — 5차 보완으로 viewer 에서 이동)
 *
 * - confirmBase 기반, footer = null (요구사항 §6-4)
 * - viewer iframe 안에서 직접 호출하면 마스크 영역이 iframe 으로 클립되므로
 *   `viewerWindow.NineNative.openReadReceiptDetailPopup(emailId)` 핸들러를 통해
 *   host 컨텍스트에서 호출되도록 한다 (viewerExt.tsx 참조).
 * - emailId 를 함께 받아 래퍼가 readReceiptListenerStore 를 구독해 refresh
 *   결과를 자동 반영하도록 한다.
 */
export function openReadReceiptDetailPopup(opts: {
  emailId: string
  initialStatus: SendStatusResponse
}) {
  return confirmBase({
    wrapClassName: classNames(styles.readReceiptDetailPopup),
    width: 400,
    centered: true,
    title: null,
    content: (
      <ReadReceiptDetailContentHost emailId={opts.emailId} initialStatus={opts.initialStatus} />
    ),
    closable: true,
    closeIcon: <img src={closeIcon24} alt="close" />,
    footer: null,
    maskClosable: true,
  })
}
